import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { signIn } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import flytLogo from "@/assets/flyt-logo.svg";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [platformIssue, setPlatformIssue] = useState<string | null>(null);
  const navigate = useNavigate();

  // Proactive health check on mount
  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const res = await supabase.functions.invoke("check-platform-status", {
          body: {},
        });
        console.log("[health-check] result:", JSON.stringify({ data: res.data, error: res.error?.message ?? res.error }));
        if (res.error || !res.data) {
          // Any failure reaching the backend = connectivity issue
          setPlatformIssue("Vi har problemer med å koble til serveren. Det kan være en pågående driftsforstyrrelse.");
        } else if (res.data?.hasActiveIncident) {
          setPlatformIssue(
            res.data.title
              ? `Kjent problem: ${res.data.title}`
              : "Det er kjente tekniske problemer med plattformen. Prøv igjen senere."
          );
        }
      } catch (e) {
        console.log("[health-check] caught:", e);
        setPlatformIssue("Vi har problemer med å koble til serveren. Det kan være en pågående driftsforstyrrelse.");
      }
    })();
    return () => controller.abort();
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setPlatformIssue(null);
    try {
      const { error } = await signIn(email, password);
      if (error) {
        // Detect network-level failures (cert errors, no connectivity)
        const isNetworkError = error.message?.toLowerCase().includes("load failed") ||
          error.message?.toLowerCase().includes("failed to fetch") ||
          error.message?.toLowerCase().includes("network") ||
          error.status === 0;

        if (isNetworkError) {
          toast.error("Kunne ikke koble til serveren");
          setPlatformIssue("Vi har problemer med å koble til serveren. Det kan være en pågående driftsforstyrrelse.");
        } else {
          toast.error("Feil e-post eller passord");
          // Check platform status in background
          try {
            const { data, error: fnError } = await supabase.functions.invoke("check-platform-status");
            if (fnError) {
              setPlatformIssue("Vi har problemer med å koble til serveren. Det kan være en pågående driftsforstyrrelse.");
            } else if (data?.hasActiveIncident) {
              setPlatformIssue(
                data.title
                  ? `Kjent problem: ${data.title}`
                  : "Det er kjente tekniske problemer med plattformen. Prøv igjen senere."
              );
            }
          } catch {
            setPlatformIssue("Vi har problemer med å koble til serveren. Det kan være en pågående driftsforstyrrelse.");
          }
        }
      } else {
        navigate("/dashboard", { replace: true });
      }
    } catch {
      toast.error("Kunne ikke koble til serveren");
      setPlatformIssue("Vi har problemer med å koble til serveren. Det kan være en pågående driftsforstyrrelse.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <Card className="w-full max-w-sm shadow-md rounded-2xl">
        <CardHeader className="text-center">
          {platformIssue && (
            <Alert variant="destructive" className="mb-4 text-left">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{platformIssue}</AlertDescription>
            </Alert>
          )}
          <img src={flytLogo} alt="Flyt" className="mx-auto mb-2 h-14 w-auto" />
          <CardTitle className="text-2xl">Velkommen til Flyt</CardTitle>
          <CardDescription>Logg inn for å fortsette med barnets plan.</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="mt-1 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">E-post</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="din@epost.no"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Passord</Label>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                minLength={6}
              />
              <Link to="/forgot-password" className="text-sm text-primary inline-block">
                Glemt passord?
              </Link>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-3">
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Logger inn…" : "Logg inn"}
            </Button>
            <p className="text-xs text-muted-foreground text-center mt-3">
              Trygt lagret i EU. Ingen tredjeparts sporing.
            </p>
            <p className="text-sm text-muted-foreground">
              Har du ikke konto?{" "}
              <Link to="/register" className="text-primary underline">
                Registrer deg
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
